import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ComplianceBadge } from '../components/div7a/ComplianceBadge';
import { Calculator, DollarSign, FileText, TrendingUp, AlertCircle, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { div7aAPI } from '../lib/div7aAPI';

export default function Div7ADashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await div7aAPI.getDashboardMetrics();
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load Division 7A dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const metricsConfig = [
    {
      title: 'Total Div 7A Exposure',
      value: `$${metrics.total_exposure.toLocaleString('en-AU')}`,
      icon: DollarSign,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Active Loans',
      value: metrics.active_loans,
      icon: FileText,
      color: 'bg-accent text-primary'
    },
    {
      title: 'MYR Due This FY',
      value: `$${metrics.myr_due_this_fy.toLocaleString('en-AU')}`,
      subtitle: `Paid: $${metrics.myr_paid_this_fy.toLocaleString('en-AU')}`,
      icon: Calculator,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Non-Compliant Loans',
      value: metrics.non_compliant_loans,
      subtitle: `${metrics.compliant_loans} compliant`,
      icon: AlertCircle,
      color: metrics.non_compliant_loans > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
    }
  ];

  const compliancePercentage = metrics.myr_due_this_fy > 0 
    ? (metrics.myr_paid_this_fy / metrics.myr_due_this_fy * 100) 
    : 100;

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-8 h-8 text-foreground" />
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="div7a-dashboard-title">
            Division 7A Compliance
          </h1>
        </div>
        <p className="text-muted-foreground mt-1">
          ATO Division 7A loan monitoring and minimum yearly repayment tracking
        </p>
      </div>

      {/* Overall Compliance Status Card */}
      <Card className="border-l-4 border-l-[#0055FF]">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                  Overall Compliance Status
                </h3>
                <ComplianceBadge status={metrics.overall_status} />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">MYR Payment Progress ({metrics.current_financial_year})</span>
                  <span className="font-semibold text-foreground">{compliancePercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      metrics.overall_status === 'compliant' ? 'bg-green-500' :
                      metrics.overall_status === 'at_risk' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${compliancePercentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Paid: ${metrics.myr_paid_this_fy.toLocaleString('en-AU')}</span>
                  <span>Due: ${metrics.myr_due_this_fy.toLocaleString('en-AU')}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsConfig.map((metric, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow duration-200" data-testid={`div7a-metric-card-${index}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                  <p className="text-3xl font-bold text-foreground mt-2" style={{fontFamily: 'IBM Plex Mono, monospace'}}>
                    {metric.value}
                  </p>
                  {metric.subtitle && (
                    <p className="text-xs text-muted-foreground mt-2">{metric.subtitle}</p>
                  )}
                </div>
                <div className={`w-12 h-12 ${metric.color} rounded-lg flex items-center justify-center`}>
                  <metric.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              onClick={() => navigate('/div7a/scanner')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-scanner"
            >
              <TrendingUp className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Scan Transactions</span>
            </Button>
            <Button 
              onClick={() => navigate('/div7a/loans')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-loans"
            >
              <FileText className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Manage Loans</span>
            </Button>
            <Button 
              onClick={() => navigate('/div7a/repayments')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-repayments"
            >
              <DollarSign className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Record Repayment</span>
            </Button>
            <Button 
              onClick={() => navigate('/clients')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-clients"
            >
              <Shield className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">View Clients</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Division 7A Compliance Requirements</h4>
              <p className="text-xs text-blue-800">
                Division 7A requires that all loans from a private company to shareholders or associates must:
              </p>
              <ul className="text-xs text-blue-800 mt-2 space-y-1 ml-4 list-disc">
                <li>Have a compliant written loan agreement in place before lodgement</li>
                <li>Charge interest at the ATO benchmark rate (currently {metrics.current_financial_year === '2024-25' ? '4.52%' : 'TBC'})</li>
                <li>Have a maximum term of 7 years (unsecured) or 25 years (secured)</li>
                <li>Require minimum yearly repayments (MYR) to be made each financial year</li>
                <li>Failure to comply results in the loan being treated as an unfranked dividend</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}